import { useEffect, useMemo, useRef, useState } from 'react';
import { FACE_EYES_Y, FACE_HEIGHT as H, FACE_LANDMARKS, FACE_MAX_BYTES, FACE_WIDTH as W } from '@karozik/shared';
import { cn, Spinner } from '@karozik/ui';
import { detectFace, eyeCentres, type Point } from './faceLandmarks';

const OVAL = { cx: W / 2, cy: 215, rx: 118, ry: 150 };
/** Distance between the eye centres in the frame (the two dots on the guide line). */
const EYE_GAP = 90;
const ZOOM = { min: 0.4, max: 6 };
const ROT = 45;
/**
 * Phone photos run to 12 MP. Every gesture redraws the photo and the detector
 * reads it whole, so both work on one copy this size: still sharp at max zoom
 * in the 360×450 frame, and an order of magnitude lighter.
 */
const MAX_SIDE = 1600;

function downscale(i: HTMLImageElement): HTMLCanvasElement {
  const k = Math.min(1, MAX_SIDE / Math.max(i.naturalWidth, i.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.round(i.naturalWidth * k);
  c.height = Math.round(i.naturalHeight * k);
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(i, 0, 0, c.width, c.height);
  return c;
}

/** Resolves after the next frame is painted, so a "busy" state shows before a long synchronous task. */
const afterPaint = () => new Promise<void>((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));

export interface CroppedFace {
  /** JPEG data URL, 360×450. */
  image: string;
  /** Face mesh in the frame (flat x,y), or null when no face was found. */
  landmarks: number[] | null;
}

type Detection = 'searching' | 'found' | 'missing';

/**
 * Frames a selfie so every face sits at the same place: eyes on the line, face
 * in the oval. MediaPipe finds the face and frames it automatically; the guest
 * can still drag, pinch, zoom and straighten. That alignment, plus the face
 * landmarks sent along, is what lets two faces be morphed into one.
 */
export function FaceCropper({ file, onDone }: { file: File; onDone(face: CroppedFace | null): void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [img, setImg] = useState<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [points, setPoints] = useState<Point[] | null>(null);
  const [detection, setDetection] = useState<Detection>('searching');
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef(0);

  useEffect(() => {
    // Ignore a load cancelled by the cleanup (StrictMode runs effects twice).
    let alive = true;
    const url = URL.createObjectURL(file);
    const i = new Image();
    i.onload = () => {
      if (!alive) return;
      const photo = downscale(i);
      setImg(photo);
      // Detection holds the main thread for a moment: the "searching" overlay
      // must be on screen first, and the controls stay locked until it ends so
      // the automatic framing never overwrites the guest's own adjustments.
      void afterPaint()
        .then(() => (alive ? detectFace(photo) : null))
        .then((pts) => {
          if (!alive) return;
          setPoints(pts);
          setDetection(pts ? 'found' : 'missing');
          if (pts) autoFrame(photo, pts);
        });
    };
    i.onerror = () => alive && setError('Cette photo ne s’ouvre pas. Essaie une photo JPEG ou PNG.');
    i.src = url;
    return () => {
      alive = false;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  const base = img ? Math.max(W / img.width, H / img.height) : 1;
  const locked = !img || detection === 'searching';

  // Background: the photo itself, blurred (drawn tiny then stretched), so a
  // zoomed-out face never sits on black bars. Built once per photo.
  const backdrop = useMemo(() => {
    if (!img) return null;
    const tiny = document.createElement('canvas');
    tiny.width = Math.ceil(W / 14);
    tiny.height = Math.ceil(H / 14);
    const cover = Math.max(tiny.width / img.width, tiny.height / img.height);
    tiny.getContext('2d')!.drawImage(img, (tiny.width - img.width * cover) / 2, (tiny.height - img.height * cover) / 2, img.width * cover, img.height * cover);
    return tiny;
  }, [img]);

  /** Where a pixel of the original photo lands in the frame, for the current framing. */
  const toFrame = (p: Point, z = zoom, r = rot, t = pos): Point => {
    if (!img) return p;
    const s = base * z;
    const a = (r * Math.PI) / 180;
    const dx = (p.x - img.width / 2) * s;
    const dy = (p.y - img.height / 2) * s;
    return { x: W / 2 + t.x + dx * Math.cos(a) - dy * Math.sin(a), y: H / 2 + t.y + dx * Math.sin(a) + dy * Math.cos(a) };
  };

  /** Puts the eyes on the guide: right spacing, level, centred. */
  function autoFrame(i: HTMLCanvasElement, pts: Point[]) {
    const b = Math.max(W / i.width, H / i.height);
    const [e1, e2] = eyeCentres(pts);
    const z = Math.min(ZOOM.max, Math.max(ZOOM.min, EYE_GAP / Math.hypot(e2.x - e1.x, e2.y - e1.y) / b));
    const r = Math.min(ROT, Math.max(-ROT, (-Math.atan2(e2.y - e1.y, e2.x - e1.x) * 180) / Math.PI));
    const s = b * z;
    const a = (r * Math.PI) / 180;
    const dx = ((e1.x + e2.x) / 2 - i.width / 2) * s;
    const dy = ((e1.y + e2.y) / 2 - i.height / 2) * s;
    setZoom(z);
    setRot(r);
    setPos({ x: -(dx * Math.cos(a) - dy * Math.sin(a)), y: FACE_EYES_Y - H / 2 - (dx * Math.sin(a) + dy * Math.cos(a)) });
  }

  useEffect(() => {
    const ctx = canvas.current?.getContext('2d');
    if (!ctx || !img || !backdrop) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(backdrop, 0, 0, W, H);
    ctx.save();
    ctx.translate(W / 2 + pos.x, H / 2 + pos.y);
    ctx.rotate((rot * Math.PI) / 180);
    ctx.scale(base * zoom, base * zoom);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, -img.width / 2, -img.height / 2);
    ctx.restore();
  }, [img, backdrop, zoom, rot, pos, base]);

  const clampZoom = (z: number) => Math.min(ZOOM.max, Math.max(ZOOM.min, z));

  const onMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (locked) return;
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const k = W / e.currentTarget.clientWidth;
    if (pointers.current.size === 1) {
      setPos((p) => ({ x: p.x + (e.clientX - prev.x) * k, y: p.y + (e.clientY - prev.y) * k }));
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      if (pinch.current) setZoom((z) => clampZoom((z * d) / pinch.current));
      pinch.current = d;
    }
  };
  const onUp = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    pinch.current = 0;
  };

  const validate = () => {
    const c = canvas.current;
    if (!c) return;
    let image = c.toDataURL('image/jpeg', 0.82);
    if (image.length > (FACE_MAX_BYTES * 4) / 3) image = c.toDataURL('image/jpeg', 0.6);
    const landmarks = points
      ? points.slice(0, FACE_LANDMARKS).flatMap((p) => {
          const q = toFrame(p);
          return [Math.round(q.x * 10) / 10, Math.round(q.y * 10) / 10];
        })
      : null;
    onDone({ image, landmarks });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-night/95 p-4 backdrop-blur" role="dialog" aria-modal="true" aria-labelledby="crop-title">
      <div className="animate-pop w-full max-w-sm rounded-3xl bg-surface p-5">
        <h2 id="crop-title" className="font-display text-xl font-bold">
          Cadre ta tête
        </h2>
        <p className={cn('mt-1 text-sm', detection === 'missing' ? 'text-sun' : 'text-ink-dim')} aria-live="polite">
          {detection === 'searching' && '🔍 On cherche ton visage…'}
          {detection === 'found' && '✨ Cadré automatiquement. Ajuste si besoin : yeux sur la ligne, visage dans l’ovale.'}
          {detection === 'missing' && 'Visage non trouvé : cadre-toi à la main. Ta tête jouera, mais pas dans les modes morphing.'}
        </p>
        {error ? (
          <p className="mt-4 rounded-xl bg-danger/15 px-4 py-3 text-sm text-danger">{error}</p>
        ) : (
          <div className="relative mt-4 aspect-[4/5] w-full touch-none overflow-hidden rounded-2xl bg-black">
            <canvas
              ref={canvas}
              width={W}
              height={H}
              className="h-full w-full cursor-grab touch-none"
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
                pinch.current = 0;
              }}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              onWheel={(e) => !locked && setZoom((z) => clampZoom(z * Math.exp(-e.deltaY * 0.0015)))}
            />
            <svg viewBox={`0 0 ${W} ${H}`} className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden>
              <path
                fill="rgba(11,6,20,.55)"
                fillRule="evenodd"
                d={`M0 0H${W}V${H}H0Z M${OVAL.cx} ${OVAL.cy - OVAL.ry} a${OVAL.rx} ${OVAL.ry} 0 1 0 0.01 0Z`}
              />
              <ellipse cx={OVAL.cx} cy={OVAL.cy} rx={OVAL.rx} ry={OVAL.ry} fill="none" stroke="#ff3cac" strokeWidth="2.5" strokeDasharray="7 6" />
              <line x1={92} y1={FACE_EYES_Y} x2={W - 92} y2={FACE_EYES_Y} stroke="#2bd2ff" strokeWidth="2" />
              <circle cx={W / 2 - EYE_GAP / 2} cy={FACE_EYES_Y} r={5} fill="#2bd2ff" />
              <circle cx={W / 2 + EYE_GAP / 2} cy={FACE_EYES_Y} r={5} fill="#2bd2ff" />
              <text x={W / 2} y={FACE_EYES_Y - 8} textAnchor="middle" fill="#2bd2ff" fontSize="12" fontWeight="700">
                YEUX ICI
              </text>
            </svg>
            {locked && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-night/60 text-sm font-bold">
                <Spinner className="h-8 w-8" />
                On cherche ton visage…
              </div>
            )}
          </div>
        )}
        <label className="mt-4 flex items-center gap-3 text-sm text-ink-dim">
          <span className="w-24">Zoom</span>
          <input type="range" min={ZOOM.min} max={ZOOM.max} step={0.01} value={zoom} disabled={locked} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1 accent-pink disabled:opacity-40" />
        </label>
        <label className="mt-2 flex items-center gap-3 text-sm text-ink-dim">
          <span className="w-24">Inclinaison</span>
          <input type="range" min={-ROT} max={ROT} step={0.5} value={rot} disabled={locked} onChange={(e) => setRot(Number(e.target.value))} className="flex-1 accent-pink disabled:opacity-40" />
        </label>
        <div className="mt-5 flex gap-2">
          <button onClick={() => onDone(null)} className="flex-1 rounded-2xl bg-surface-2 py-3">
            Annuler
          </button>
          <button onClick={validate} disabled={locked} className="bg-gradient-neon flex-[2] rounded-2xl py-3 font-bold disabled:opacity-40">
            Valider ma tête
          </button>
        </div>
      </div>
    </div>
  );
}
